import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faLinkedinIn,
  faInstagram,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faFax, faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';

import avvoIcon from '../../assets/icons/avvo-icon.svg';
import { withLang } from '../../utils/withLang';

import './footer.scss';

export default function Footer() {
  const { t } = useTranslation('footer');
  const location = useLocation();
  const pathname = location.pathname;

  const [mapLink1, setMapLink1] = useState('https://maps.app.goo.gl/vo4roG97VMWUyYQd9');
  const [mapLink2, setMapLink2] = useState(
    'https://www.google.com/maps/search/?api=1&query=11231+Gold+Express+Dr,+Unit+103,+Gold+River,+CA+95670',
  );

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    setMapLink1(
      isIOS
        ? 'https://maps.apple.com/place?address=600%20N%20Brand%20Blvd,%20Glendale,%20CA%2091203'
        : 'https://maps.app.goo.gl/vo4roG97VMWUyYQd9',
    );

    setMapLink2(
      isIOS
        ? 'https://maps.apple.com/?q=11231+Gold+Express+Dr,+Unit+103,+Gold+River,+CA+95670'
        : 'https://www.google.com/maps/search/?api=1&query=11231+Gold+Express+Dr,+Unit+103,+Gold+River,+CA+95670',
    );
  }, []);

  return (
    <footer className="site-footer">
      <div className="footer-main">
        <Container>
          <Row>
            {/* Quick Links + Social */}
            <Col md={4} className="mb-4 mb-md-0">
              <p className="footer-heading">{t('moreFromUs')}</p>
              <ul className="footer-list">
                <li>
                  <Link to={withLang(pathname, '/privacy-policy')}>
                    {t('quickLinks.PrivacyPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to={withLang(pathname, '/accessibility')}>
                    {t('quickLinks.AccessibilityStatement')}
                  </Link>
                </li>
                <li>
                  <Link to={withLang(pathname, '/legal-disclaimer')}>
                    {t('quickLinks.LegalDisclaimer')}
                  </Link>
                </li>
                <li>
                  <Link to={withLang(pathname, '/faq')}>{t('quickLinks.FAQ')}</Link>
                </li>
              </ul>

              <div className="social-icons">
                <a
                  href="https://www.facebook.com/AvagyanLaw/"
                  aria-label="Avagyan Law Firm on Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faFacebookF} aria-hidden="true" />
                </a>

                <a
                  href="https://www.instagram.com/avagyanlaw/?hl=en"
                  aria-label="Avagyan Law Firm on Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faInstagram} aria-hidden="true" />
                </a>

                <a
                  href="https://www.tiktok.com/@avagyanlaw"
                  aria-label="Avagyan Law Firm on TikTok"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faTiktok} aria-hidden="true" />
                </a>

                <a
                  href="https://www.avvo.com/attorneys/91205-ca-tehmina-avagyan-5185982.html"
                  aria-label="Avagyan Law Firm Avvo profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={avvoIcon}
                    alt="avoIcon"
                    width={30}
                    height={30}
                    aria-hidden="true"
                    className="avvo-icon"
                  />
                </a>

                <a
                  href="https://www.linkedin.com/company/avagyanlaw"
                  aria-label="Avagyan Law Firm on LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faLinkedinIn} aria-hidden="true" />
                </a>
              </div>
            </Col>

            {/* Glendale Office */}
            <Col md={4} className="mb-4 mb-md-0">
              <p className="footer-heading">{t('officeTitle1')}</p>
              <address className="footer-address">
                {/*
                  The icon and the address text used to be two separate JSX
                  expressions ({' '} then {t(...)} on the next line), which
                  compile to two separate text-node children. react-snap
                  prerenders by capturing a real browser's outerHTML as a
                  plain string, and a plain HTML string has no way to mark
                  where one text node ends and an adjacent one begins - two
                  adjacent text nodes always serialize as one continuous run
                  of characters. So every real visitor's browser parses that
                  string back into a single merged text node, while React's
                  hydration still expects two separate children and throws
                  "Minified React error #418" the instant it hits this
                  element. Combining both into one expression (below) means
                  there is only ever one child, so there is nothing for
                  prerendering to lose in translation. This exact element -
                  the very first thing rendered inside <footer>, which
                  appears on every single page - is what was crashing
                  hydration on the live site.
                */}
                <p>
                  <a
                    href={mapLink1}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open map for ${t('office.address1')}`}
                  >
                    <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                    {` ${t('office.address1')}`}
                  </a>
                </p>

                <p>
                  <a
                    href="tel:+18056000006"
                    aria-label="Call Glendale office at +1 (805) 600-0006"
                    onClick={() =>
                      ReactGA.event({
                        category: 'User',
                        action: 'Clicked Phone Call',
                        label: 'Footer Call Glendale',
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faPhone} aria-hidden="true" /> +1 (805) 600-0006
                  </a>
                </p>

                <p>
                  <a href="mailto:info@avagyanlaw.com" aria-label="Email info@avagyanlaw.com">
                    <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> info@avagyanlaw.com
                  </a>
                </p>

                <p>
                  <FontAwesomeIcon icon={faFax} aria-hidden="true" />
                  {` ${t('office.fax')}`}
                </p>
              </address>
            </Col>

            {/* Sacramento Office */}
            <Col md={4}>
              <p className="footer-heading">{t('officeTitle2')}</p>
              <address className="footer-address">
                <p>
                  <a
                    href={mapLink2}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open map for ${t('office.address2')}`}
                  >
                    <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                    {` ${t('office.address2')}`}
                  </a>
                </p>

                <p>
                  <a
                    href="tel:+19169999909"
                    aria-label="Call Sacramento office at +1 (916) 999-9909"
                    onClick={() =>
                      ReactGA.event({
                        category: 'User',
                        action: 'Clicked Phone Call Sacramento',
                        label: 'Footer Call Sacramento',
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faPhone} aria-hidden="true" /> +1 (916) 999-9909
                  </a>
                </p>

                <p>
                  <a href="mailto:info@avagyanlaw.com" aria-label="Email info@avagyanlaw.com">
                    <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> info@avagyanlaw.com
                  </a>
                </p>
              </address>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <Container>
          {/*
            Same class of bug as the address links above: "©", the year, and
            "Avagyan Law Firm" used to be three separate JSX children (two
            literal text runs plus the {new Date()...} expression) with
            nothing but a plain-text/expression boundary between them - a
            boundary that has no representation once react-snap serializes
            the rendered DOM to an HTML string, so every visitor's browser
            parsed it back as a single merged text node and hydration
            mismatched. One combined expression means one child, always.
          */}
          <p className="mb-0 text-center">{`© ${new Date().getFullYear()} Avagyan Law Firm`}</p>
          <p className="site-credit">
            {'Made by '}
            <a
              href="https://www.linkedin.com/in/mkrtum-abrahamyan/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Mkrtum Abrahamyan LinkedIn profile"
            >
              Mkrtum Abrahamyan
            </a>
          </p>
        </Container>
      </div>
    </footer>
  );
}
